/**
 * Wire protocol for the HITL approval pause (ADR-050, published by ADR-091).
 *
 * Two directions: the workflow streams an {@link SdkApprovalRequest} as a
 * `data-approval-request` chunk, and the client POSTs an
 * {@link SdkApprovalResponse} to the resolution route
 * (`/api/sdk/v1/tool-approvals`, or the app's `/api/chat/tool-approvals`).
 * Everything that decides, persists or stores trust (`HitlPolicy`, the
 * settings rows, trust-key semantics, the webhook key/TTL) stays
 * server-only. `@workspace/ai/capabilities/approval-types` re-exports these.
 */

import { z } from "zod";

/** A destructive sub-op of a batch invocation, as shown on the card. */
export const sdkSubOpForDisplaySchema = z.object({
  args: z.record(z.string(), z.unknown()),
  /** Pre-rendered summary string with `**bold**` markers. */
  summary: z.string(),
  tool: z.string(),
});

export type SdkSubOpForDisplay = z.infer<typeof sdkSubOpForDisplaySchema>;

/**
 * One labeled row of a structured approval preview (e.g. a meeting's
 * "Title" / "When" / "Attendees"). Values may carry `**bold**` markers;
 * rendered as a definition list on the approval card.
 */
export const sdkApprovalPreviewFieldSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export type SdkApprovalPreviewField = z.infer<
  typeof sdkApprovalPreviewFieldSchema
>;

/**
 * A tool-declared arg the user may edit ON the approval card before
 * approving. The card renders an input per field and sends changed values
 * back as `SdkApprovalResponse.editedArgs`; the workflow filters them
 * through its own sanitizer — only declared keys, only kind-conformant
 * values, ever reach the tool. A preview row whose label matches an
 * editable field's label is replaced by the input.
 */
export const sdkApprovalEditableFieldSchema = z.object({
  /**
   * Value the card displays when the arg is absent from the call — the
   * tool's effective default. Display-only; an untouched field is never
   * sent back as an edit.
   */
  absentValue: z.unknown().optional(),
  /** Top-level key in the tool's args this field edits. */
  key: z.string(),
  /**
   * Input control + sanitization contract:
   *   - `text` / `textarea`: string, length-capped
   *   - `boolean`: checkbox
   *   - `attachments`: list of chat-file references
   *     (`{ uploadId | outputFilename, name? }`), addable/removable with
   *     the standard upload control
   */
  kind: z.enum(["text", "textarea", "boolean", "attachments"]),
  /** Input label; also matches away the duplicate preview row. */
  label: z.string(),
  placeholder: z.string().optional(),
});

export type SdkApprovalEditableField = z.infer<
  typeof sdkApprovalEditableFieldSchema
>;

/**
 * Workflow → client. Emitted as an ephemeral `data-approval-request` chunk
 * when a destructive tool invocation cannot be auto-approved. Never
 * persisted into message history; a reload re-derives it from the resume
 * stream's replay.
 */
export const sdkApprovalRequestSchema = z.object({
  /**
   * Floor flag (server-computed): this invocation prompts on every call —
   * the card must not offer "Always Allow" or "Approve everything" for it.
   */
  alwaysRequireApproval: z.boolean(),
  args: z.record(z.string(), z.unknown()),
  capabilityId: z.string(),
  /** Args the user may edit on the card. Absent → a pure approve/deny preview. */
  editableFields: z.array(sdkApprovalEditableFieldSchema).optional(),
  /**
   * Keys that will be persisted as `"always"` if the user picks Always
   * Allow. Informational only: the workflow persists from its own copy of
   * this list — the client never echoes keys back.
   */
  persistKeysIfAlways: z.array(z.string()),
  /** Structured preview rows (pre-rendered server-side), when declared. */
  previewFields: z.array(sdkApprovalPreviewFieldSchema).optional(),
  /** Destructive sub-ops for a batch invocation; empty for a single tool. */
  subOps: z.array(sdkSubOpForDisplaySchema),
  /** Pre-rendered summary string with `**bold**` markers. */
  summary: z.string(),
  /** The surface the chat originated on; same as the storage key prefix. */
  surface: z.string(),
  toolCallId: z.string(),
  toolName: z.string(),
});

export type SdkApprovalRequest = z.infer<typeof sdkApprovalRequestSchema>;

/**
 *   - `allow`:  the workflow resumes and executes the tool.
 *   - `always`: like `allow`, plus the workflow persists the
 *     server-computed `persistKeysIfAlways` (from the original request)
 *     as "always" before resuming. The keys are never taken from the client.
 *   - `approve-all`: like `allow`, plus the workflow persists
 *     `dangerouslyApproveAll` for the request's surface — every future
 *     HITL prompt on that surface is skipped, except invocations declared
 *     `alwaysRequireApproval` (the floor beats the bypass). Refused
 *     server-side when the request was floored.
 *   - `deny`:   the workflow returns a tool error (ADR-048 soft-deny).
 */
export const sdkApprovalDecisionSchema = z.enum([
  "allow",
  "always",
  "approve-all",
  "deny",
]);

export type SdkApprovalDecision = z.infer<typeof sdkApprovalDecisionSchema>;

/**
 * Client → server: the body of the resolution route. Parsing strips every
 * other key, which is what keeps the client from ever supplying persistence
 * keys or a server-only `reason` (ADR-091 invariant 1).
 */
export const sdkApprovalResponseSchema = z.object({
  decision: sdkApprovalDecisionSchema,
  /**
   * Values the user changed on the card before approving, keyed by arg
   * name. Only meaningful on allow-decisions; the workflow sanitizes them
   * against the request's `editableFields` before merging into the call.
   */
  editedArgs: z.record(z.string(), z.unknown()).optional(),
  toolCallId: z.string().min(1),
});

export type SdkApprovalResponse = z.infer<typeof sdkApprovalResponseSchema>;

/** Success body of the resolution route. Errors are HTTP-status-driven. */
export const sdkApprovalResultSchema = z.object({ ok: z.literal(true) });

export type SdkApprovalResult = z.infer<typeof sdkApprovalResultSchema>;

/**
 * How long the server waits for a human decision before a background sweep
 * auto-denies the prompt (ms). Published for client expiry UX only;
 * enforcement is the server sweep's.
 */
export const APPROVAL_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * The payload the workflow's approval hook receives: the client's decision,
 * plus `reason`, which only the stale-approval sweep sets (`expired`: nobody
 * answered within {@link APPROVAL_TIMEOUT_MS}). The resolution routes strip
 * it, so a `deny` without a reason is always a user's click.
 */
export type ApprovalResponse = SdkApprovalResponse & { reason?: "expired" };

/**
 * The shape the workflow's approval hook receives. Distinct from the
 * `data-approval-request` chunk — that one tells the *client* to prompt;
 * this one tells the *workflow* what the user picked.
 */
export type ApprovalWebhookPayload = ApprovalResponse;
